<template>
  <Base>
    <template v-slot:toolbar>
      <div class="toolbar" id="kt_toolbar">
        <div class="container-fluid d-flex flex-stack" id="kt_toolbar_container">
          <div class="d-flex align-items-center me-3">
            <span class="h-20px border-gray-200 border-start mx-4"></span>
            <ul class="breadcrumb breadcrumb-separatorless fw-bold fs-7 my-1">
              <li class="breadcrumb-item text-muted">
                <router-link :to="{ name: 'dashboard' }" class="text-muted text-hover-primary">
                  <i class="fad fa-home"></i>
                  {{ trans('Dashboard') }}
                </router-link>
              </li>
              <li class="breadcrumb-item">
                <span class="bullet bg-gray-300 w-5px h-2px"></span>
              </li>
              <li class="breadcrumb-item text-muted">
                <router-link :to="{ name: 'campaign-list' }" class="text-muted text-hover-primary">
                  Campagnes
                </router-link>
              </li>
              <li class="breadcrumb-item">
                <span class="bullet bg-gray-300 w-5px h-2px"></span>
              </li>
              <li class="breadcrumb-item text-dark">
                Logs des jobs
              </li>
            </ul>
          </div>

          <div class="d-flex align-items-center py-1">
            <router-link :to="{ name: 'campaign-create', params: { id: campaignId } }" class="btn btn-sm btn-light-primary me-3">
              <i class="fad fa-arrow-left"></i>
              Retour à la campagne
            </router-link>
            <button class="btn btn-sm btn-primary" @click="load">
              <i class="fad fa-sync"></i>
              Rafraîchir
            </button>
          </div>
        </div>
      </div>
    </template>

    <template #content>
      <!-- Header avec nom de campagne -->
      <div class="d-flex justify-content-between align-items-center mb-5">
        <div>
          <h1 class="fs-2 fw-bolder mb-1">{{ campaign.name }}</h1>
          <span class="text-muted">Code: {{ campaign.code }}</span>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="row g-5 mb-5">
        <div class="col-md-3">
          <div class="card bg-light-primary">
            <div class="card-body py-4">
              <div class="d-flex align-items-center">
                <div class="symbol symbol-50px me-3">
                  <span class="symbol-label bg-primary">
                    <i class="fad fa-paper-plane text-white fs-2"></i>
                  </span>
                </div>
                <div>
                  <div class="fs-4 fw-bolder">{{ stats.total }}</div>
                  <div class="text-muted fs-7">Total jobs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card bg-light-success">
            <div class="card-body py-4">
              <div class="d-flex align-items-center">
                <div class="symbol symbol-50px me-3">
                  <span class="symbol-label bg-success">
                    <i class="fad fa-check text-white fs-2"></i>
                  </span>
                </div>
                <div>
                  <div class="fs-4 fw-bolder">{{ stats.sent }}</div>
                  <div class="text-muted fs-7">Envoyés</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card bg-light-danger">
            <div class="card-body py-4">
              <div class="d-flex align-items-center">
                <div class="symbol symbol-50px me-3">
                  <span class="symbol-label bg-danger">
                    <i class="fad fa-times text-white fs-2"></i>
                  </span>
                </div>
                <div>
                  <div class="fs-4 fw-bolder">{{ stats.failed }}</div>
                  <div class="text-muted fs-7">Échoués</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card bg-light-warning">
            <div class="card-body py-4">
              <div class="d-flex align-items-center">
                <div class="symbol symbol-50px me-3">
                  <span class="symbol-label bg-warning">
                    <i class="fad fa-clock text-white fs-2"></i>
                  </span>
                </div>
                <div>
                  <div class="fs-4 fw-bolder">{{ stats.pending }}</div>
                  <div class="text-muted fs-7">En attente</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Filtres -->
      <div class="card mb-5">
        <div class="card-body py-4">
          <div class="row g-3 align-items-center">
            <div class="col-md-3">
              <label class="form-label fw-bold">Type</label>
              <select v-model="filters.type" class="form-select form-select-sm" @change="load">
                <option value="">Tous</option>
                <option value="sms">SMS</option>
                <option value="email">Email</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-bold">Status</label>
              <select v-model="filters.status" class="form-select form-select-sm" @change="load">
                <option value="">Tous</option>
                <option value="SENT">Envoyés</option>
                <option value="FAILED">Échoués</option>
                <option value="CREATED">En attente</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-bold">Limite</label>
              <select v-model="filters.limit" class="form-select form-select-sm" @change="load">
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
                <option :value="200">200</option>
              </select>
            </div>
            <div class="col-md-3 d-flex align-items-end">
              <button class="btn btn-sm btn-light-primary w-100" @click="resetFilters">
                <i class="fad fa-undo me-1"></i>
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Liste des jobs -->
      <div class="card">
        <div class="card-header border-0 pt-5">
          <h3 class="card-title align-items-start flex-column">
            <span class="card-label fw-bolder fs-3 mb-1">Historique des jobs</span>
            <span class="text-muted mt-1 fw-bold fs-7">{{ jobs.length }} job(s) affiché(s)</span>
          </h3>
        </div>
        <div class="card-body py-3">
          <div class="table-responsive">
            <table class="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
              <thead>
                <tr class="fw-bolder text-muted">
                  <th class="min-w-50px">ID</th>
                  <th class="min-w-80px">Type</th>
                  <th class="min-w-100px">Status</th>
                  <th class="min-w-150px">Destinataires</th>
                  <th class="min-w-120px">Template</th>
                  <th class="min-w-100px">Tentatives</th>
                  <th class="min-w-150px">Créé le</th>
                  <th class="min-w-150px">Traité le</th>
                  <th class="min-w-200px">Erreur</th>
                  <th class="min-w-80px text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="jobs.length === 0">
                  <td colspan="10" class="text-center text-muted py-10">
                    <i class="fad fa-inbox fs-2x mb-3 d-block"></i>
                    Aucun job trouvé
                  </td>
                </tr>
                <tr v-for="job in jobs" :key="job.type + '_' + job.id">
                  <td>
                    <span class="text-dark fw-bolder">#{{ job.id }}</span>
                  </td>
                  <td>
                    <span :class="getTypeBadgeClass(job.type)" class="badge">
                      <i :class="getTypeIcon(job.type)" class="me-1"></i>
                      {{ job.type }}
                    </span>
                  </td>
                  <td>
                    <span :class="getStatusBadgeClass(job.status)" class="badge">
                      {{ getStatusLabel(job.status) }}
                    </span>
                  </td>
                  <td>
                    <div class="d-flex flex-column">
                      <template v-if="job.recipients && job.recipients.length > 0">
                        <span v-for="(recipient, idx) in job.recipients.slice(0, 2)" :key="idx" class="text-dark fs-7">
                          {{ recipient }}
                        </span>
                        <span v-if="job.recipients.length > 2" class="text-muted fs-8">
                          +{{ job.recipients.length - 2 }} autre(s)
                        </span>
                      </template>
                      <span v-else class="text-muted">-</span>
                    </div>
                  </td>
                  <td>
                    <span v-if="job.template" class="text-dark fs-7">{{ job.template.name }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <span class="badge badge-light-dark">{{ job.attempts }}</span>
                  </td>
                  <td>
                    <span class="text-muted fs-7">{{ formatDate(job.createdAt) }}</span>
                  </td>
                  <td>
                    <span class="text-muted fs-7">{{ formatDate(job.processedAt) }}</span>
                  </td>
                  <td>
                    <div v-if="job.errorMessage" class="text-danger fs-8" style="max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" :title="job.errorMessage">
                      {{ job.errorMessage }}
                    </div>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td class="text-end">
                    <button class="btn btn-icon btn-light-primary btn-sm" @click="showJobDetail(job)" title="Voir détails">
                      <i class="fad fa-eye"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="d-flex justify-content-between align-items-center mt-5" v-if="totalPages > 1">
            <div class="text-muted">
              Page {{ currentPage }} sur {{ totalPages }}
            </div>
            <div class="d-flex gap-2">
              <button class="btn btn-sm btn-light-primary" :disabled="!canGoPrev" @click="prevPage">
                <i class="fad fa-chevron-left"></i>
                Precedent
              </button>
              <button class="btn btn-sm btn-light-primary" :disabled="!canGoNext" @click="nextPage">
                Suivant
                <i class="fad fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal détails job -->
      <b-modal id="modal-job-detail" size="lg" centered title="Détails du job" hide-footer>
        <div v-if="selectedJob">
          <div class="row mb-4">
            <div class="col-6">
              <label class="form-label fw-bold">ID</label>
              <div>#{{ selectedJob.id }}</div>
            </div>
            <div class="col-6">
              <label class="form-label fw-bold">Type</label>
              <div>
                <span :class="getTypeBadgeClass(selectedJob.type)" class="badge">{{ selectedJob.type }}</span>
              </div>
            </div>
          </div>
          <div class="row mb-4">
            <div class="col-6">
              <label class="form-label fw-bold">Status</label>
              <div>
                <span :class="getStatusBadgeClass(selectedJob.status)" class="badge">{{ getStatusLabel(selectedJob.status) }}</span>
              </div>
            </div>
            <div class="col-6">
              <label class="form-label fw-bold">Tentatives</label>
              <div>{{ selectedJob.attempts }}</div>
            </div>
          </div>
          <div class="row mb-4">
            <div class="col-6">
              <label class="form-label fw-bold">Créé le</label>
              <div>{{ formatDate(selectedJob.createdAt) }}</div>
            </div>
            <div class="col-6">
              <label class="form-label fw-bold">Traité le</label>
              <div>{{ formatDate(selectedJob.processedAt) || '-' }}</div>
            </div>
          </div>
          <div class="mb-4">
            <label class="form-label fw-bold">Destinataires</label>
            <div class="bg-light rounded p-3">
              <div v-for="(recipient, idx) in selectedJob.recipients" :key="idx">{{ recipient }}</div>
              <span v-if="!selectedJob.recipients || selectedJob.recipients.length === 0" class="text-muted">Aucun</span>
            </div>
          </div>
          <div class="mb-4" v-if="selectedJob.template">
            <label class="form-label fw-bold">Template</label>
            <div>{{ selectedJob.template.name }} (ID: {{ selectedJob.template.id }})</div>
          </div>
          <div class="mb-4" v-if="selectedJob.errorMessage">
            <label class="form-label fw-bold text-danger">Message d'erreur</label>
            <div class="bg-light-danger text-danger rounded p-3">{{ selectedJob.errorMessage }}</div>
          </div>
          <div class="mb-4" v-if="selectedJob.apiResponse">
            <label class="form-label fw-bold">Réponse API</label>
            <pre class="bg-light rounded p-3 fs-8" style="max-height: 200px; overflow: auto;">{{ JSON.stringify(selectedJob.apiResponse, null, 2) }}</pre>
          </div>
        </div>
      </b-modal>
    </template>
  </Base>
</template>

<script lang="ts">
import Base from "@/layouts/Base.vue";
import { Vue, Component } from "vue-property-decorator";
import { api } from "@/services/Api";

@Component({
  components: { Base },
})
export default class CampaignJobs extends Vue {
  campaign: any = { name: "", code: "" };
  jobs: any[] = [];
  stats: any = {
    total: 0,
    sent: 0,
    failed: 0,
    pending: 0,
    sms: { total: 0, sent: 0, failed: 0, pending: 0 },
    email: { total: 0, sent: 0, failed: 0, pending: 0 },
    whatsapp: { total: 0, sent: 0, failed: 0, pending: 0 },
  };
  filters = {
    type: "",
    status: "",
    limit: 50,
    offset: 0,
  };
  selectedJob: any = null;

  get currentPage(): number {
    return Math.floor(this.filters.offset / this.filters.limit) + 1;
  }

  get totalPages(): number {
    return Math.ceil(this.stats.total / this.filters.limit);
  }

  get canGoPrev(): boolean {
    return this.filters.offset > 0;
  }

  get canGoNext(): boolean {
    return this.filters.offset + this.filters.limit < this.stats.total;
  }

  goToPage(page: number) {
    this.filters.offset = (page - 1) * this.filters.limit;
    this.load();
  }

  prevPage() {
    if (this.canGoPrev) {
      this.filters.offset -= this.filters.limit;
      this.load();
    }
  }

  nextPage() {
    if (this.canGoNext) {
      this.filters.offset += this.filters.limit;
      this.load();
    }
  }

  get campaignId(): string {
    return this.$route.params.id as string;
  }

  mounted() {
    this.load();
  }

  async load() {
    this.$store.commit("loading");

    let url = `job/campaign/${this.campaignId}?limit=${this.filters.limit}&offset=${this.filters.offset}`;
    if (this.filters.type) {
      url += `&type=${this.filters.type}`;
    }
    if (this.filters.status) {
      url += `&status=${this.filters.status}`;
    }

    const res = await api.get(api.com, url);

    if (res && res.data) {
      this.campaign = res.data.campaign || { name: "", code: "" };
      this.jobs = res.data.jobs || [];
      this.stats = res.data.stats || {
        total: 0,
        sent: 0,
        failed: 0,
        pending: 0,
      };
    }

    this.$store.commit("stopLoading");
  }

  resetFilters() {
    this.filters = {
      type: "",
      status: "",
      limit: 50,
      offset: 0,
    };
    this.load();
  }

  showJobDetail(job: any) {
    this.selectedJob = job;
    this.$bvModal.show("modal-job-detail");
  }

  getTypeBadgeClass(type: string): string {
    switch (type?.toUpperCase()) {
      case "SMS":
        return "badge-light-warning";
      case "EMAIL":
        return "badge-light-primary";
      case "WHATSAPP":
        return "badge-light-success";
      default:
        return "badge-light-dark";
    }
  }

  getTypeIcon(type: string): string {
    switch (type?.toUpperCase()) {
      case "SMS":
        return "fad fa-sms";
      case "EMAIL":
        return "fad fa-envelope";
      case "WHATSAPP":
        return "fab fa-whatsapp";
      default:
        return "fad fa-paper-plane";
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status?.toUpperCase()) {
      case "SENT":
        return "badge-light-success";
      case "FAILED":
        return "badge-light-danger";
      case "CREATED":
        return "badge-light-warning";
      default:
        return "badge-light-dark";
    }
  }

  getStatusLabel(status: string): string {
    switch (status?.toUpperCase()) {
      case "SENT":
        return "Envoyé";
      case "FAILED":
        return "Échoué";
      case "CREATED":
        return "En attente";
      default:
        return status;
    }
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}
</script>

<style scoped>
.symbol-label {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 0.475rem;
}
</style>
